'use client';

import { useActionState, useEffect, useRef } from 'react';
import { Input, Textarea } from '@heroui/react';
import { gtag } from '@/utils/gtagPush';
import { usePathname } from 'next/navigation';
import { hotjar } from 'react-hotjar';
import { usePlausible } from 'next-plausible';
import { sendEmail } from '@/lib/email-actions';
import LeviButton from '@/app/components/LeviButton';
import FormAntiSpamFields from '@/app/components/FormAntiSpamFields';

const initialState = {
  error: undefined as string | undefined,
  success: false,
};

const inputClassNames = {
  inputWrapper: 'border-black border-2 hover:border-black/80! transition! bg-white/60',
};

export default function SupplierContactForm({ className }: { className?: string }) {
  const [state, formAction] = useActionState(sendEmail, initialState);
  const gaEventSent = useRef(false);

  const plausible = usePlausible();

  const currentPath = usePathname();

  useEffect(() => {
    if (state?.success && !gaEventSent.current)
    {
      gtag('event', 'generate_lead', { event_category: 'contact_form', event_label: currentPath, value: 1 });
      if (hotjar.initialized())
      {
        hotjar.event('successfully_submitted_form');
      }
      plausible('contact_form_submit', { props: { path: currentPath, form: 'suppliers' } });
      gaEventSent.current = true;
    }
  }, [currentPath, plausible, state?.success]);

  return (
    <div className={ `${ className } text-center md:text-left` }>
      {
        state?.success ? <div className={ 'mt-4 text-xl' }><p className={ 'text-green-700' }>Máme to. 🫡 Mrkneme na to a brzy se ozveme.</p></div>
          : <form className={ 'w-full relative' } action={ formAction } autoComplete={ 'on' } name={ 'contact' }>
            <input type={ 'hidden' } name={ 'source' } value={ 'pro-dodavatele' } />
            <div className={ 'flex flex-col md:flex-row gap-2' }>
              <Input type={ 'text' } data-hj-allow variant={ 'bordered' } color={ 'default' } radius={ 'sm' } required isRequired label={ 'Firma' } autoComplete={ 'organization' } classNames={ inputClassNames } className={ 'w-full' } name={ 'company' } onFocus={ () => {
                hotjar.event('suppliers-contact_form-focused_company_input');
                plausible('contact_form-focused_company_input', { props: { path: currentPath, form: 'suppliers' } });
              } } />

              <Input type={ 'text' } data-hj-allow variant={ 'bordered' } color={ 'default' } radius={ 'sm' } required isRequired label={ 'Jméno' } autoComplete={ 'name' } classNames={ inputClassNames } className={ 'w-full' } name={ 'name' } onFocus={ () => {
                hotjar.event('suppliers-contact_form-focused_name_input');
                plausible('contact_form-focused_name_input', { props: { path: currentPath, form: 'suppliers' } });
              } } />
            </div>
            <div className={ 'mt-2 flex flex-col md:flex-row gap-2' }>
              <Input type={ 'tel' } data-hj-allow variant={ 'bordered' } required={ false } color={ 'default' } radius={ 'sm' } label={ 'Telefon' } autoComplete={ 'tel' } classNames={ inputClassNames } className={ 'w-full' } name={ 'phone' } onFocus={ () => {
                hotjar.event('suppliers-contact_form-focused_phone_input');
                plausible('contact_form-focused_phone_input', { props: { path: currentPath, form: 'suppliers' } });
              } } />

              <Input type={ 'email' } data-hj-allow variant={ 'bordered' } color={ 'default' } radius={ 'sm' } required isRequired label={ 'E-mail' } autoComplete={ 'email' } classNames={ inputClassNames } className={ 'w-full' } name={ 'email' } onFocus={ () => {
                hotjar.event('suppliers-contact_form-focused_email_input');
                plausible('contact_form-focused_email_input', { props: { path: currentPath, form: 'suppliers' } });
              } } />
            </div>
            <Textarea placeholder={ 'Napište třeba, co je váš hlavní produkt nebo co chcete prodávat' } data-hj-allow variant={ 'bordered' } label={ 'Komentář' } color={ 'default' } radius={ 'sm' } minRows={ 4 } className={ 'w-full mt-2' } name={ 'message' } classNames={ inputClassNames } onFocus={ () => {
              hotjar.event('suppliers-contact_form-focused_message_input');
              plausible('contact_form-focused_message_input', { props: { path: currentPath, form: 'suppliers' } });
            } } />
            <FormAntiSpamFields resetKey={ state } />
            <LeviButton color={ 'primary' } type={ 'submit' } className={ 'mt-6 w-full!' } onClick={ () => {
              hotjar.event('suppliers-contact_form-clicked_send');
              plausible('contact_form-clicked_send', { props: { path: currentPath, form: 'suppliers' } });
            } }>Chci návrh zdarma</LeviButton>
            { state?.error && <p className={ `mt-4 text-danger` }>{ state.error }</p> }
          </form>
      }
    </div>
  );
}
